import type { VariantConflict } from '@open-pencil/core/editor'
import type { ComponentPropertyDefinition, SceneNode } from '@open-pencil/scene-graph'

import { useEditor } from '#react/editor/context'
import { useSceneComputed } from '#react/internal/scene-computed/use'

export interface VariantDefinitionControl {
  id: string
  name: string
  values: string[]
}

function variantContext(node: SceneNode | null, graph: ReturnType<typeof useEditor>['graph']) {
  if (node?.type === 'COMPONENT_SET') return { componentSet: node, variant: null }
  if (node?.type !== 'COMPONENT' || !node.parentId) return null
  const parent = graph.getNode(node.parentId)
  return parent?.type === 'COMPONENT_SET' ? { componentSet: parent, variant: node } : null
}

/** Variant dimensions on a component set — same surface as Vue `useVariantAuthoring`. */
export function useVariantAuthoring() {
  const editor = useEditor()
  const context = useSceneComputed(() => {
    const selected = editor.getSelectedNodes()
    return selected.length === 1 ? variantContext(selected[0] ?? null, editor.graph) : null
  })
  const active = context !== null
  const componentSet = context?.componentSet ?? null
  const variant = context?.variant ?? null
  const definitions = useSceneComputed<VariantDefinitionControl[]>(() => {
    const componentSetId = componentSet?.id
    if (!componentSetId) return []
    const values = editor.collectVariantOptions(componentSetId)
    return editor
      .getComponentSetPropertyDefs(componentSetId)
      .filter(
        (definition): definition is ComponentPropertyDefinition => definition.type === 'VARIANT'
      )
      .map((definition) => ({
        id: definition.id,
        name: definition.name,
        values: [...(values.get(definition.name) ?? [])]
      }))
  })
  const diagnostics = useSceneComputed<VariantConflict[]>(() => {
    const componentSetId = componentSet?.id
    return componentSetId ? editor.getComponentSetVariantConflicts(componentSetId) : []
  })

  /**
   * Add a variant property, by default named Property 1, Property 2, … with the value Default,
   * as Figma does; returns its id so the caller can start renaming it.
   */
  function addProperty(name?: string, initialValue = 'Default'): string | undefined {
    const componentSetId = componentSet?.id
    if (!componentSetId) return undefined
    return editor.addPropertyDefinition(
      componentSetId,
      name ?? nextPropertyName(),
      'VARIANT',
      initialValue
    )
  }

  function nextPropertyName(): string {
    const taken = new Set(definitions.map((definition) => definition.name))
    let index = 1
    while (taken.has(`Property ${index}`)) index++
    return `Property ${index}`
  }

  function renameProperty(propertyId: string, name: string) {
    const componentSetId = componentSet?.id
    return componentSetId
      ? editor.renamePropertyDefinition(componentSetId, propertyId, name)
      : false
  }

  function removeProperty(propertyId: string) {
    const componentSetId = componentSet?.id
    return componentSetId ? editor.removePropertyDefinition(componentSetId, propertyId) : false
  }

  function reorderProperties(propertyIds: string[]) {
    const componentSetId = componentSet?.id
    return componentSetId ? editor.reorderPropertyDefinitions(componentSetId, propertyIds) : false
  }

  function renameValue(propertyId: string, previousValue: string, value: string) {
    const componentSetId = componentSet?.id
    return componentSetId
      ? editor.renameVariantValue(componentSetId, propertyId, previousValue, value)
      : false
  }

  function reorderValues(propertyId: string, values: string[]) {
    const componentSetId = componentSet?.id
    return componentSetId ? editor.reorderVariantValues(componentSetId, propertyId, values) : false
  }

  function setVariantValue(propertyId: string, value: string) {
    const variantId = variant?.id
    return variantId
      ? editor.setVariantPropertyValue(variantId, propertyId, value)
      : { kind: 'invalid' as const }
  }

  function addVariant() {
    const componentSetId = componentSet?.id
    return componentSetId ? editor.addVariant(componentSetId) : undefined
  }

  function duplicateVariant() {
    const source = variant ?? editor.getDefaultVariantForComponentSet(componentSet?.id ?? '')
    return source ? editor.duplicateVariant(source.id) : undefined
  }

  function removeVariant() {
    return variant ? editor.removeVariant(variant.id) : false
  }

  return {
    active,
    componentSet,
    variant,
    definitions,
    diagnostics,
    addProperty,
    renameProperty,
    removeProperty,
    reorderProperties,
    renameValue,
    reorderValues,
    setVariantValue,
    addVariant,
    duplicateVariant,
    removeVariant
  }
}
